'use client'

import { memo, useLayoutEffect, useRef } from 'react'
import { usePathname } from 'next/navigation'
import styled from 'styled-components'
import { createUnderline, designGrid, mq, presets, rgbAlpha, vw } from '@/styles'

type TypeContentText = {
  col: { from: number, expand: number },
  row: { from: number, expand: number },
  align?: {
    v: 'top' | 'center' | 'bottom',
    h: 'left' | 'center' | 'right'
  },
  color?: string,
  text: string,
  type: 'text'
}

type Props = {
  data: TypeContentText
}

const contactString = [
  '/page/find-us-here',
  '/page/contactanos',
  '/page/trouvez-nous',
  '/page/finden-sie-uns-hier',
  '/page/donde-estamos',
  '/page/encontre-nos-aqui'
]

export const ContentText = memo(({ data }:Props) => {
  const { text, color, align } = data
  const pathname = usePathname()
  const ref = useRef<HTMLDivElement>(null)

  // This chunk allows inner embed scripts to run
  useLayoutEffect(() => {
    const range = document.createRange()
    range.selectNode(ref.current)
    const documentFragment = range.createContextualFragment(text)

    ref.current.innerHTML = ''
    ref.current.append(documentFragment)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <TextContent ref={ref} className={`text-wrapper ${contactString.indexOf(pathname)  !== -1 ? 'contact' : ''}`} $color={color} $align={align} dangerouslySetInnerHTML={{ __html: text }} />
  )
})

const TextContent = styled.div<{ $color: TypeContentText['color'], $align?: TypeContentText['align'] }>`
  color: ${({ $color }) => $color ? $color : 'var(--color-text)'};
  text-align: ${({ $align }) => $align && $align.h ? $align.h : 'left'};
  word-break: break-word;
  width: 100%;

  a {
    ${createUnderline({ height: 1, reverse: true })}
  }

  h2, h3, h4,
  p, ul, ol, dl,
  q, a {
    color: inherit;
    text-align: inherit;
    word-break: inherit;
  }

  p, ul, ol, dl, q, a {
    color: var(--color-text--darker);
  }

  h2, h3, h4, h5, h6 {
    color: var(--color-text);
  }

  h2, h3, h4 {
    font-weight: 500;
    margin-bottom: ${vw(16, 'mobile')};

    ${mq.greaterThan('nexus7')} {
      margin-bottom: ${vw(16, 'tablet')};
    }

    ${mq.greaterThan('tablet')} {
      margin-bottom: ${vw(20, 'desktop')};
    }

    ${mq.greaterThan('desktop')} {
      margin-bottom: 20px;
    }

    + p {
      margin-top: -${vw(10, 'mobile')};

      ${mq.greaterThan('nexus7')} {
        margin-top: -${vw(10, 'tablet')};
      }

      ${mq.greaterThan('tablet')} {
        margin-top: -${vw(10, 'desktop')};
      }

      ${mq.greaterThan('desktop')} {
        margin-top: -10px;
      }
    }
  }

  h1, h2 {
    ${presets.font30()}

    br {
      + p, ul, ol, dl, h2, h3, h4, h5, h6, a {
        margin-top:  ${vw(15, 'mobile')};

        ${mq.greaterThan('nexus7')} {
          margin-top: ${vw(15, 'tablet')};
        }

        ${mq.greaterThan('tablet')} {
          margin-top: ${vw(20, 'desktop')};
        }

        ${mq.greaterThan('desktop')} {
          margin-top: 20px;
        }
      }
    }
  }

  h3 {
    ${presets.font26_1()}

    br {
      + p, ul, ol, dl, h2, h3, h4, h5, h6, a {
        margin-top:  ${vw(15, 'mobile')};

        ${mq.greaterThan('nexus7')} {
          margin-top: ${vw(15, 'tablet')};
        }

        ${mq.greaterThan('tablet')} {
          margin-top: ${vw(15, 'desktop')};
        }

        ${mq.greaterThan('desktop')} {
          margin-top: 15px;
        }
      }
    }
  }

  h4 {
    ${presets.font18_1()}
  }

  h5 {
    ${presets.font15()}
  }

  h4, h5 {
    br {
      + p, ul, ol, dl, h2, h3, h4, h5, h6, a {
        margin-top:  ${vw(10, 'mobile')};

        ${mq.greaterThan('nexus7')} {
          margin-top: ${vw(10, 'tablet')};
        }

        ${mq.greaterThan('tablet')} {
          margin-top: ${vw(10, 'desktop')};
        }

        ${mq.greaterThan('desktop')} {
          margin-top: 10px;
        }
      }
    }
  }

  h6 {
    ${presets.font12_1()}

    br {
      + p, ul, ol, dl, h2, h3, h4, h5, h6, a {
        margin-top:  ${vw(8, 'mobile')};

        ${mq.greaterThan('nexus7')} {
          margin-top: ${vw(8, 'tablet')};
        }

        ${mq.greaterThan('tablet')} {
          margin-top: ${vw(8, 'desktop')};
        }

        ${mq.greaterThan('desktop')} {
          margin-top: 8px;
        }
      }
    }
  }

  p {
    ${presets.font18_1()}

    br {
      content: '';
      display: block;
      margin-bottom:  ${vw(10, 'mobile')};

      ${mq.greaterThan('nexus7')} {
        margin-bottom: ${vw(10, 'tablet')};
      }

      ${mq.greaterThan('tablet')} {
        margin-bottom: ${vw(15, 'desktop')};
      }

      ${mq.greaterThan('desktop')} {
        margin-bottom: 15px;
      }
    }
  }

  ul, ol, dl {
    ${presets.font20()}
    font-weight: normal;
    margin-bottom: ${vw(48, 'mobile')};

    ${mq.greaterThan('nexus7')} {
      margin-bottom: ${vw(48, 'tablet')};
    }

    ${mq.greaterThan('tablet')} {
      margin-bottom: ${vw(60, 'desktop')};
    }

    ${mq.greaterThan('desktop')} {
      margin-bottom: 60px;
    }

    &:last-child {
      margin-bottom: 0;
    }

    br {
      + p, ul, ol, dl, h2, h3, h4, h5, h6, a {
        margin-top:  ${vw(10, 'mobile')};

        ${mq.greaterThan('nexus7')} {
          margin-top: ${vw(10, 'tablet')};
        }

        ${mq.greaterThan('tablet')} {
          margin-top: ${vw(15, 'desktop')};
        }

        ${mq.greaterThan('desktop')} {
          margin-top: 15px;
        }
      }
    }
  }




  br {
    + p, ul, ol, dl, h2, h3, h4, h5, h6, a {
      ${presets.font20_4()}
    }
  }

  ul, ol {
    padding-left: 18px;
  }

  ul {
    li {
      list-style-type: disc;
    }
  }

  ol {
    li {
      list-style-type: decimal;
    }
  }

  q, blockquote {
    ${presets.font53()}
    border-left: 0;
    color: var(--color-text);
    display: block;
    font-weight: bold;
    font-style: normal;
    margin: auto;
    padding: ${vw(12, 'mobile')} ${vw(20, 'mobile')} 0;
    position: relative;
    text-align: center;
    width: 100%;

    ${mq.greaterThan('nexus7')} {
      padding: ${vw(12, 'tablet')} ${vw(20, 'tablet')} 0;
    }

    ${mq.greaterThan('tablet')} {
      padding: ${vw(20, 'desktop')} 0 ${vw(20, 'desktop')};
      transform: translateX(-${vw(120, 'desktop')});
      width: calc(100% + ${vw(120 * 2, 'desktop')});
    }

    ${mq.greaterThan('desktop')} {
      padding: 20px 0 20px;
      transform: translateX(-120px);
      width: calc(100% + 240px);
    }

    &:before,
    &:after {
      content: '';
      background-image: url('/images/svg/i--double-quotes.svg');
      background-repeat: no-repeat;
      display: inline-block;
      height: ${vw(33, 'mobile')};
      opacity: .2;
      position: absolute;
      user-select: none;
      width: ${vw(47, 'mobile')};

      ${mq.greaterThan('nexus7')} {
        height: ${vw(33, 'tablet')};
        width: ${vw(47, 'tablet')};
      }

      ${mq.greaterThan('tablet')} {
        height: ${vw(66, 'desktop')};
        width: ${vw(94, 'desktop')};
      }

      ${mq.greaterThan('desktop')} {
        height: 66px;
        width: 94px;
      }
    }

    &:before {
      left: ${vw(20, 'mobile')};
      top: -${vw(10, 'mobile')};
      transform: rotate(180deg);

      ${mq.greaterThan('nexus7')} {
        left: ${vw(20, 'tablet')};
        top: -${vw(10, 'tablet')};
      }

      ${mq.greaterThan('tablet')} {
        left: ${vw(30, 'desktop')};
        top: -${vw(10, 'desktop')};
      }

      ${mq.greaterThan('desktop')} {
        left: 30px;
        top: -10px;
      }
    }

    &:after {
      bottom: -${vw(10, 'mobile')};
      right: ${vw(20, 'mobile')};

      ${mq.greaterThan('nexus7')} {
        bottom: -${vw(10, 'tablet')};
        right: ${vw(30, 'tablet')};
      }

      ${mq.greaterThan('tablet')} {
        bottom: 0;
        right: ${vw(30, 'desktop')};
      }

      ${mq.greaterThan('desktop')} {
        right: 30px;
      }
    }

    > p {
      ${presets.font53()}
      color:var(--color-text);
      display: inline;
      font-style: normal;
      text-align: center;
    }
  }

  table {
    border-collapse: collapse;
    width: 100%;

    th,
    td {
      ${presets.font20()}
      padding: ${vw(20, 'mobile')} ${vw(5, 'mobile')};

      ${mq.greaterThan('nexus7')} {
        padding: ${vw(20, 'tablet')} ${vw(5, 'tablet')};
      }

      ${mq.greaterThan('tablet')} {
        padding: ${vw(30.5, 'desktop')} ${vw(10, 'desktop')};
      }

      ${mq.greaterThan('desktop')} {
        padding: 30.5px 10px;
      }

      &:first-child {
        padding-left: 0;
      }

      &:last-child {
        padding-right: 0;
      }
    }

    th {
      color: var(--color-text);
      border-bottom: 1px solid ${rgbAlpha('--color-text', .6)};
    }

    tbody, tfoot {
      td {
        border-bottom: 1px solid ${rgbAlpha('--color-text', .1)};
        color: ${rgbAlpha('--color-text', .6)};

        ${mq.greaterThan('tablet')} {
          max-width: ${vw(490, 'desktop')};
          vertical-align: initial;
        }

        ${mq.greaterThan('desktop')} {
          max-width: 490px;
        }
      }

      tr {
        &:last-child {
          td {
            border-color: ${rgbAlpha('--color-text', 0)};
          }
        }
      }
    }
  }

  &.contact {
    ${designGrid({})}
    align-items: baseline;
    padding: 0;
    margin-top: ${vw(60, 'mobile')};

    &:first-child {
      margin-top: 0;
    }

    /* p:first-of-type {
      display: none;
    } */

    h3, b {
      ${presets.font26_1()}
    }
    > section {
      margin-bottom: 0;
    }

    h3 {
      grid-area: 1 / 1 / span 1 / span 1;
      text-align: center;
      font-size: 1rem;
      ${mq.greaterThan('tablet')}{
        text-align: left;
      }
    }
    h4.spacer_contact {
      grid-area: auto / 1 / span 1 / span 1 !important;
    }
    p {
      display: inline-grid;
      ${mq.lessThan('tablet')}{
        display: inline-grid;
        grid-column: 2 / span 5;
        padding: 0 ${vw(20, 414)}vw 0 ${vw(13, 'mobile')};
      }

      &.nop {
        display: none;
      }
    }

    span {
      font-size: ${vw(15, 'mobile')};
      line-height: ${vw(20, 'mobile')};
      letter-spacing: -.5px;
      font-weight: 400;

      ${mq.greaterThan('tablet')}{
        font-size: ${vw(18, 'desktop')};
        line-height: ${vw(25, 'desktop')};
      }
    }

    p {
      margin-bottom: ${vw(37, 'mobile')};

      b, span {
        display: block;
        margin-bottom: ${vw(15, 'mobile')};

        ${mq.greaterThan('tablet')}{
          margin-bottom: ${vw(15, 'desktop')};
        }
      }
    }

    ${mq.greaterThan('tablet')}{
      margin-top: ${vw(60, 'desktop')};

      h3 {
        font-size: unset;
        grid-area: 1 / 2 / span 1 / span 2;
      }
      h4 {
        grid-area:2 / 1 / span 1 / span 1 !important;
      }
      p {
        display: inline-grid;
        grid-column-end: span 3;
        margin-bottom: ${vw(60, 'desktop')};
        align-items: end;

        &.nop {
          display: block;
          opacity: 0;
          grid-column-end: span 2;
        }
      }
    }
  }

  .panettone_container {
    display: flex;
    flex-direction: column;
    gap: 40px;

    .panettone_card {
      align-items: center;
      display: flex;
      flex-direction: column;
      gap: 30px;
      justify-content: flex-start;

      ${mq.greaterThan('tablet')} {
        flex-direction: row;
      }

      > img {
        width: 50%;

        ${mq.greaterThan('tablet')} {
          width: 170px;
        }

      }

      > p {
        ${presets.font18_1()}
        width: 100%;

        ${mq.greaterThan('tablet')} {
            width: 55%;
        }

      }

      > button {
        ${presets.font15()}
        align-items: center;
        background-color: var(--color-text);
        color: var(--color-white);
        display: flex;
        height:${vw(50, 'mobile')};
        justify-content: center;
        letter-spacing: 0;
        margin: 0 auto;
        padding:${vw(17, 'mobile')} ${vw(30, 'mobile')};
        position: relative;
        width:unset;

        ${mq.greaterThan('nexus7')} {
          height: ${vw(50, 'tablet')};
          padding:${vw(17, 'tablet')} ${vw(30, 'tablet')};
        }

        ${mq.greaterThan('tablet')} {
          height:${vw(60, 'desktop')};
          padding: ${vw(19, 'desktop')} ${vw(43, 'desktop')};
        }

        ${mq.greaterThan('desktop')} {
          height:60px;
          padding: 19px 43px;
        }

        &,
        &:before {
          transition: 300ms background-color ease-in-out, 300ms border ease-in-out, 300ms color ease-in-out, 300ms filter ease-in-out, 300ms opacity ease-in-out;
          border-radius:${vw(30, 'mobile')};

          ${mq.greaterThan('nexus7')} {
            border-radius:${vw(30, 'tablet')};
          }

          ${mq.greaterThan('tablet')} {
            border-radius:${vw(30, 'desktop')};
          }

          ${mq.greaterThan('desktop')} {
            border-radius: 30px;
          }
        }

        > div:last-of-type {
          ${presets.font15()}
          align-items: center;
          color: inherit;
          display: flex;
          height: 100%;
          justify-content: center;
          width: 100%;
          z-index: 2;
          transition: transform 0.4s cubic-bezier(0.1, 0, 0.3, 1);
          white-space: nowrap;

          img {
            padding: ${vw(10, 'mobile')};

            ${mq.greaterThan('nexus7')} {
              padding: ${vw(10, 'tablet')};
            }

            ${mq.greaterThan('tablet')} {
              padding: ${vw(10, 'desktop')};
            }

            ${mq.greaterThan('desktop')} {
              padding: 10px;
            }
          }
        }

        .button__bg {
          background: var(--color-text);
          border-radius:${vw(30, 'mobile')};
          border: none;
          height: 100%;
          left: 0;
          overflow: hidden;
          position: absolute;
          top: 0;
          transition: transform 0.4s cubic-bezier(0.1, 0, 0.3, 1);
          width: 100%;

          ${mq.greaterThan('nexus7')} {
            border-radius:${vw(30, 'tablet')};
          }

          ${mq.greaterThan('tablet')} {
            border-radius:${vw(30, 'desktop')};
          }

          ${mq.greaterThan('desktop')} {
            border-radius: 30px;
          }

          &:before,
          &:after {
            content: '';
            position: absolute;
            z-index: -1;
            background-color: var(--color-text--darker);
          }

          &:before {
            width: 110%;
            height: 0;
            padding-bottom: 110%;
            top: 50%;
            left: 50%;
            border-radius: 50%;
            transform: translate3d(-50%,-50%,0) scale3d(0,0,1);
          }

          &:after {
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            opacity: 0;
            transition: opacity 0.3s;
          }
        }

        &:hover {
          color: var(--color-white);

          .button__bg {
            transform: 'none';

            ${mq.greaterThan('tablet')} {
              transform: scale3d(1.1,1.2,1);
            }

            &:before {
              transition: transform 0.4s cubic-bezier(0.1, 0, 0.3, 1);
              transform: translate3d(-50%,-50%,0) scale3d(1,1,1);
            }

            &:after {
              opacity: 1;
              transition-duration: 0.01s;
              transition-delay: 0.3s;
            }
          }
        }

      }
    }
  }
`
